import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery } from '../../../types/pagination';
import type { ApiClient } from '../client';
import type { ApiResponse, BorrowBookRequest, BorrowBookResponse, CreateLibraryAuthorRequest, CreateLibraryAuthorResponse, CreateLibraryBookRequest, CreateLibraryBookResponse, CreateLibraryCategoryRequest, CreateLibraryCategoryResponse, CreateLibraryMemberRequest, CreateLibraryMemberResponse, CreateLibraryReservationRequest, CreateLibraryReservationResponse, ListLibraryAuthorsResponse, ListLibraryBooksResponse, ListLibraryBorrowsQuery, ListLibraryBorrowsResponse, ListLibraryCategoriesResponse, ListLibraryMemberCandidatesParams, ListLibraryMemberCandidatesResponse, ListLibraryMembersParams, ListLibraryMembersResponse, ListLibraryReservationsResponse, PayLibraryFineRequest, RenewBorrowRequest, RenewBorrowResponse, ReturnBookRequest } from '../types';

export function bindLibrary(client: ApiClient) {
  return {
async listLibraryAuthors(params?: PaginationQuery): Promise<ApiResponse<ListLibraryAuthorsResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const query = qs.toString();
    const path = query ? `/library/authors?${query}` : '/library/authors';
    return client.request<ListLibraryAuthorsResponse>(path);
  },

async createLibraryAuthor(body: CreateLibraryAuthorRequest): Promise<ApiResponse<CreateLibraryAuthorResponse>> {
    return client.request<CreateLibraryAuthorResponse>('/library/authors', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listLibraryBooks(params?: PaginationQuery): Promise<ApiResponse<ListLibraryBooksResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const query = qs.toString();
    const path = query ? `/library/books?${query}` : '/library/books';
    return client.request<ListLibraryBooksResponse>(path);
  },

async createLibraryBook(body: CreateLibraryBookRequest): Promise<ApiResponse<CreateLibraryBookResponse>> {
    return client.request<CreateLibraryBookResponse>('/library/books', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listLibraryCategories(): Promise<ApiResponse<ListLibraryCategoriesResponse>> {
    return client.request<ListLibraryCategoriesResponse>('/library/categories');
  },

async createLibraryCategory(body: CreateLibraryCategoryRequest): Promise<ApiResponse<CreateLibraryCategoryResponse>> {
    return client.request<CreateLibraryCategoryResponse>('/library/categories', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listLibraryMembers(
    params?: ListLibraryMembersParams
  ): Promise<ApiResponse<ListLibraryMembersResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (k === 'page' || k === 'limit' || k === 'offset') return;
        if (v === undefined || v === null) return;
        const s = String(v).trim();
        if (!s) return;
        qs.set(k, s);
      });
    }
    const query = qs.toString();
    const path = query ? `/library/members?${query}` : '/library/members';
    return client.request<ListLibraryMembersResponse>(path);
  },

async listLibraryMemberCandidates(
    params: ListLibraryMemberCandidatesParams
  ): Promise<ApiResponse<ListLibraryMemberCandidatesResponse>> {
    const qs = new URLSearchParams();
    qs.set('member_type', params.member_type);
    if (params.q?.trim()) qs.set('q', params.q.trim());
    if (params.class_id) qs.set('class_id', params.class_id);
    if (params.section_id) qs.set('section_id', params.section_id);
    if (params.exclude_members) qs.set('exclude_members', 'true');
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    return client.request<ListLibraryMemberCandidatesResponse>(`/library/members/candidates?${qs.toString()}`);
  },

async createLibraryMember(body: CreateLibraryMemberRequest): Promise<ApiResponse<CreateLibraryMemberResponse>> {
    return client.request<CreateLibraryMemberResponse>('/library/members', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listLibraryBorrows(params?: ListLibraryBorrowsQuery): Promise<ApiResponse<ListLibraryBorrowsResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    if (params?.status) qs.set('status', params.status);
    if (params?.search?.trim()) qs.set('search', params.search.trim());
    const query = qs.toString();
    const path = query ? `/library/borrows?${query}` : '/library/borrows';
    return client.request<ListLibraryBorrowsResponse>(path);
  },

async borrowLibraryBook(body: BorrowBookRequest): Promise<ApiResponse<BorrowBookResponse>> {
    return client.request<BorrowBookResponse>('/library/borrow', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async renewLibraryBorrow(body: RenewBorrowRequest): Promise<ApiResponse<RenewBorrowResponse>> {
    return client.request<RenewBorrowResponse>('/library/borrows/renew', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async returnLibraryBook(body: ReturnBookRequest): Promise<ApiResponse<void>> {
    return client.request<void>('/library/return', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async payLibraryFine(body: PayLibraryFineRequest): Promise<ApiResponse<void>> {
    return client.request<void>('/library/fines/pay', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listLibraryReservations(params?: PaginationQuery): Promise<ApiResponse<ListLibraryReservationsResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const query = qs.toString();
    const path = query ? `/library/reservations?${query}` : '/library/reservations';
    return client.request<ListLibraryReservationsResponse>(path);
  },

async createLibraryReservation(
    body: CreateLibraryReservationRequest
  ): Promise<ApiResponse<CreateLibraryReservationResponse>> {
    return client.request<CreateLibraryReservationResponse>('/library/reservations', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  };
}
